'use client';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  updateProfile,
  type AppDownloadPromptState,
  type HomeSurfaceState,
  type PromptsState,
  type ReportPromptState,
  type ReviewPromptState,
  type SlugSurfaceState,
  type UiState,
} from '@repo/api';
// The bucket-shape types above (HomeSurfaceState, SlugSurfaceState)
// are used as return annotations inside onPageView — they exist
// even though they're not visible at the top-level scope.
import { useStore } from '@/providers/StoreProvider';
import {
  HOME_VIEW_DEBOUNCE_MS,
  PROMPT_THRESHOLDS,
  isAppDownloadShownForSurface,
  isAppDownloadSurface,
  isMobileWebViewport,
  isReportShownForSurface,
  isReportSurface,
  isReviewShownForSurface,
  mergeUiState,
  purgeSeenIfShown,
  readLocalUiState,
  writeLocalUiState,
  type AppDownloadSurface,
  type PromptType,
  type ReportSurface,
  type TriggerPageType,
} from '@/lib/prompts/storage';

const TRIGGER_DELAY_MS = 5_000;
const BE_SYNC_DEBOUNCE_MS = 2_000;

interface PromptContextValue {
  /**
   * Called from trigger pages on mount. Increments the counter for
   * `pageType` (deduping by slug where applicable), schedules the 5s
   * timer if a threshold is crossed, and stores the triggering
   * building slug for post-purchase redirect (report prompt only).
   */
  onPageView: (pageType: TriggerPageType, slug: string, triggerReportReturnUrl?: string) => void;
  /** Which prompt is currently open, if any. Rendered by PromptOverlay. */
  activePrompt: PromptType | null;
  /**
   * When `activePrompt === 'review'`, tells the modal which surface
   * fired the prompt so it can (a) send the right `screen` value with
   * the analytics event and (b) mark the correct per-surface shown
   * flag on dismiss. Null while no review prompt is active.
   */
  activeReviewScreen: TriggerPageType | null;
  /**
   * When `activePrompt === 'report'`, tells the modal which surface
   * fired the prompt (unit or building) so it can tag analytics and
   * flip the right per-surface shown flag. Null while no report
   * prompt is active.
   */
  activeReportScreen: ReportSurface | null;
  /** URL to redirect to after successful subscription purchase (report prompt only). */
  triggerReportReturnUrl: string | null;
  /** Dismiss the currently-open prompt (marks shown, persists). */
  dismissActivePrompt: () => void;
  /**
   * Mark a prompt as shown (called after CTA click). Both prompts are
   * per-surface, so pass the surface that fired so we set the correct
   * flag. Defaults to 'unit' when omitted.
   */
  markPromptShown: (prompt: PromptType, surface?: TriggerPageType) => void;
}

const PromptContext = createContext<PromptContextValue | null>(null);

export function usePromptContext(): PromptContextValue {
  const ctx = useContext(PromptContext);
  if (!ctx) throw new Error('usePromptContext must be used inside PromptProvider');
  return ctx;
}

export function PromptProvider({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const sessionHydrated = useStore(s => s.webAuth.sessionHydrated);
  // We watch both `token` and `customToken` because Firebase-token
  // auth mode uses either as the bearer. Without this, our POST to
  // /profile/modify/ can race the moment `isLoggedIn` flips to true
  // — the auth interceptor still has null (see setApiAuthToken in
  // AuthEffects) so the request goes out unauthed, gets 403'd, and
  // the response interceptor logs the user out.
  const authToken = useStore(s => s.webAuth.token);
  const authCustomToken = useStore(s => s.webAuth.customToken);
  const hasAuthToken = Boolean(authToken || authCustomToken);
  const profile = useStore(s => s.profile.profile);
  const accessType = useStore(s => s.explorer.data?.accessType);
  const hasReviewerAccess = useStore(s => s.reviewer.data?.hasReviewerAccess);

  const [uiState, setUiState] = useState<UiState>(() => readLocalUiState());
  const [activePrompt, setActivePrompt] = useState<PromptType | null>(null);
  const [activeReviewScreen, setActiveReviewScreen] = useState<TriggerPageType | null>(null);
  const [activeReportScreen, setActiveReportScreen] = useState<ReportSurface | null>(null);
  const [triggerReportReturnUrl, setTriggerReportReturnUrl] = useState<string | null>(null);

  // Timers keyed by prompt type. Live in a ref so they survive
  // re-renders + route navigation. When the user views a trigger page,
  // we schedule a 5s setTimeout that later opens the prompt — the
  // page they were on when it fires doesn't matter.
  const pendingTimersRef = useRef<Partial<Record<PromptType, ReturnType<typeof setTimeout>>>>({});

  // Debounced BE sync — batches counter increments so we don't PATCH
  // profile on every page view.
  const beSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSyncRef = useRef<UiState | null>(null);
  const hasSyncedOnLoginRef = useRef(false);

  // Eligibility per prompt. Logged-out users are always eligible.
  // Logged-in users are eligible only if they don't already have the
  // access the prompt is asking them to acquire.
  const isEligibleForReviewPrompt = !isLoggedIn || !hasReviewerAccess;
  const isEligibleForReportPrompt = !isLoggedIn || !accessType;

  const persist = useCallback(
    (next: UiState) => {
      // Local persist happens immediately whether logged in or out.
      // For logged-in users, localStorage keeps mirroring BE so that a
      // future logout on this browser still respects the shown flags.
      writeLocalUiState(next);

      // Skip BE sync unless we have BOTH the logged-in flag AND the
      // bearer token available. In Firebase-token mode the token can
      // lag `isLoggedIn` by a tick during hydration; firing this
      // without a token gets 403'd and logs the user out.
      if (!isLoggedIn || !hasAuthToken) return;

      // Debounced BE sync.
      pendingSyncRef.current = next;
      if (beSyncTimerRef.current) clearTimeout(beSyncTimerRef.current);
      beSyncTimerRef.current = setTimeout(() => {
        const snapshot = pendingSyncRef.current;
        pendingSyncRef.current = null;
        beSyncTimerRef.current = null;
        if (!snapshot) return;
        void updateProfile({ uiState: snapshot }).catch(() => {
          // Silent — retry on next state change.
        });
      }, BE_SYNC_DEBOUNCE_MS);
    },
    [isLoggedIn, hasAuthToken],
  );

  // Sync-on-login: when the user logs in AND the profile has arrived
  // with a uiState, merge BE + local, write merged to both. Runs once
  // per login session. Gated on `hasAuthToken` so we don't POST to
  // /profile/modify/ before the bearer is set on the API client — an
  // unauthed POST there 403s and boots the user out.
  useEffect(() => {
    if (!sessionHydrated) return;
    if (!isLoggedIn) return;
    if (!hasAuthToken) return;
    if (hasSyncedOnLoginRef.current) return;
    if (!profile) return;

    const beState = (profile.uiState as UiState | null) ?? {};
    const merged = mergeUiState(uiState, beState);
    // Merged state includes any freshly-added counters from the current
    // session. Write it back both places so BE and local converge.
    setUiState(merged);
    persist(merged);
    hasSyncedOnLoginRef.current = true;
    // Only depend on the login boundary — don't re-run when uiState
    // updates during the same session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, hasAuthToken, sessionHydrated, profile]);

  // Reset the "synced on login" latch when the user logs out, so the
  // next login triggers another merge.
  useEffect(() => {
    if (!isLoggedIn) {
      hasSyncedOnLoginRef.current = false;
    }
  }, [isLoggedIn]);

  // Clean up any pending timers on unmount.
  useEffect(() => {
    return () => {
      Object.values(pendingTimersRef.current).forEach(t => t && clearTimeout(t));
      if (beSyncTimerRef.current) clearTimeout(beSyncTimerRef.current);
    };
  }, []);

  const schedulePrompt = useCallback((prompt: PromptType, screen: TriggerPageType) => {
    // If already open or already scheduled, don't double-schedule.
    if (pendingTimersRef.current[prompt]) return;

    pendingTimersRef.current[prompt] = setTimeout(() => {
      delete pendingTimersRef.current[prompt];
      // Re-check eligibility at fire time (user may have logged in
      // / acquired access during the 5s wait).
      setUiState((current: UiState) => {
        if (prompt === 'review') {
          // For review, "already shown" is per-surface.
          if (isReviewShownForSurface(current.prompts, screen)) return current;
          setActivePrompt(existing => existing ?? prompt);
          setActiveReviewScreen(existing => existing ?? screen);
          return current;
        }
        if (prompt === 'appDownload') {
          if (!isAppDownloadSurface(screen)) return current;
          if (isAppDownloadShownForSurface(current.prompts, screen)) return current;
          // Re-assert the mobile gate: the viewport can widen during
          // the 5s wait, and this prompt must never open on desktop.
          if (!isMobileWebViewport()) return current;
          setActivePrompt(existing => existing ?? prompt);
          return current;
        }
        // Report is per-surface too — the unit and building buckets
        // are independent.
        if (!isReportSurface(screen)) return current;
        if (isReportShownForSurface(current.prompts, screen)) return current;
        setActivePrompt(existing => existing ?? prompt);
        setActiveReportScreen(existing => existing ?? screen);
        return current;
      });
    }, TRIGGER_DELAY_MS);
  }, []);

  const onPageView = useCallback(
    (pageType: TriggerPageType, slug: string, reportReturnUrl?: string) => {
      setUiState((current: UiState) => {
        const currentPrompts: PromptsState = current.prompts ?? {};
        const currentReview: ReviewPromptState = currentPrompts.review ?? {};
        const currentReport: ReportPromptState = currentPrompts.report ?? {};
        const currentAppDownload: AppDownloadPromptState = currentPrompts.appDownload ?? {};

        // A surface with no review threshold doesn't fire the review
        // prompt, so we don't track views for it either. `reportSurface`
        // is null on surfaces the report prompt was never wired for
        // (home / pSEO).
        const reviewThreshold = PROMPT_THRESHOLDS.review[pageType];
        const reportSurface: ReportSurface | null = isReportSurface(pageType) ? pageType : null;
        // Null everywhere except home — the app-download prompt is a
        // home-only surface today.
        const appDownloadSurface: AppDownloadSurface | null = isAppDownloadSurface(pageType)
          ? pageType
          : null;
        const appDownloadThreshold =
          appDownloadSurface === null
            ? undefined
            : PROMPT_THRESHOLDS.appDownload[appDownloadSurface];
        // Undefined when the surface is wired for the report prompt but
        // isn't currently firing it (building — it shows the review
        // prompt instead). The bucket still increments so the counter
        // survives a switch back.
        const reportThreshold =
          reportSurface === null ? undefined : PROMPT_THRESHOLDS.report[reportSurface];

        const reviewBucket = reviewThreshold === undefined ? undefined : currentReview[pageType];
        const reportBucket = reportSurface ? currentReport[reportSurface] : undefined;

        // Build the next review bucket for this surface — either a
        // slug-tracked one (unit/pseo) or the timestamp-debounced home
        // variant. Also build a next report bucket on the surfaces
        // that fire it.
        let nextReviewBucket: SlugSurfaceState | HomeSurfaceState | undefined;
        let nextReportBucket: SlugSurfaceState | undefined;
        let nextAppDownloadBucket: HomeSurfaceState | undefined;
        let changed = false;

        if (pageType === 'home') {
          const homeBucket = reviewBucket as HomeSurfaceState | undefined;
          const now = Date.now();
          const lastAt = homeBucket?.last_view_at ? new Date(homeBucket.last_view_at).getTime() : 0;
          if (now - lastAt > HOME_VIEW_DEBOUNCE_MS) {
            nextReviewBucket = {
              count: (homeBucket?.count ?? 0) + 1,
              last_view_at: new Date(now).toISOString(),
              ...(homeBucket?.shown ? { shown: homeBucket.shown } : {}),
            };
            changed = true;
          }

          // The app-download bucket debounces off its own timestamp
          // rather than the review one, mirroring how report keeps a
          // `seen` list independent of review's on the slug surfaces.
          if (appDownloadSurface) {
            const appBucket = currentAppDownload[appDownloadSurface];
            const lastAppAt = appBucket?.last_view_at
              ? new Date(appBucket.last_view_at).getTime()
              : 0;
            if (now - lastAppAt > HOME_VIEW_DEBOUNCE_MS) {
              nextAppDownloadBucket = {
                count: (appBucket?.count ?? 0) + 1,
                last_view_at: new Date(now).toISOString(),
                ...(appBucket?.shown ? { shown: appBucket.shown } : {}),
              };
              changed = true;
            }
          }
        } else {
          // unit / building / pseo — dedupe by slug.
          if (reviewThreshold !== undefined) {
            const slugBucket = reviewBucket as SlugSurfaceState | undefined;
            const alreadySeenForReview = (slugBucket?.seen ?? []).includes(slug);
            if (!alreadySeenForReview) {
              nextReviewBucket = {
                count: (slugBucket?.count ?? 0) + 1,
                seen: [...(slugBucket?.seen ?? []), slug],
                ...(slugBucket?.shown ? { shown: slugBucket.shown } : {}),
              };
              changed = true;
            }
          }

          // Track the view in the report bucket independently so
          // "seen" for report doesn't interfere with review (they
          // could purge on different schedules).
          if (reportSurface) {
            const alreadySeenForReport = (reportBucket?.seen ?? []).includes(slug);
            if (!alreadySeenForReport) {
              nextReportBucket = {
                count: (reportBucket?.count ?? 0) + 1,
                seen: [...(reportBucket?.seen ?? []), slug],
                ...(reportBucket?.shown ? { shown: reportBucket.shown } : {}),
              };
              changed = true;
            }
          }
        }

        // App-download prompt fires on the 1st home view, mobile web
        // only — desktop shows nothing.
        //
        // Deliberately checked BEFORE the `changed` bail-out below.
        // The viewport gate means a desktop view increments the counter
        // and burns the 30s debounce without ever scheduling; if this
        // sat with the other threshold checks, the user narrowing to
        // mobile and reloading inside that window would be swallowed
        // and the prompt would never appear.
        if (
          appDownloadSurface &&
          appDownloadThreshold !== undefined &&
          isMobileWebViewport() &&
          !isAppDownloadShownForSurface(currentPrompts, appDownloadSurface) &&
          (nextAppDownloadBucket?.count ?? currentAppDownload[appDownloadSurface]?.count ?? 0) >=
            appDownloadThreshold
        ) {
          schedulePrompt('appDownload', appDownloadSurface);
        }

        if (!changed) return current;

        // 2. Compose the next prompts tree.
        const nextReview: ReviewPromptState = { ...currentReview };
        if (nextReviewBucket) {
          nextReview[pageType] = nextReviewBucket;
        }
        const nextReport: ReportPromptState = { ...currentReport };
        if (reportSurface && nextReportBucket) {
          nextReport[reportSurface] = nextReportBucket;
        }
        const nextAppDownload: AppDownloadPromptState = { ...currentAppDownload };
        if (appDownloadSurface && nextAppDownloadBucket) {
          nextAppDownload[appDownloadSurface] = nextAppDownloadBucket;
        }
        // Spread `currentPrompts` first so sibling keys we don't rebuild
        // below survive. Composing this object from scratch silently wiped
        // them on every page view.
        const nextPrompts: PromptsState = {
          ...currentPrompts,
          review: nextReview,
          report: nextReport,
          appDownload: nextAppDownload,
        };

        // 3. Check thresholds → schedule prompt(s) if crossed.

        // Report prompt fires on the 2nd unit view. Per-surface — check
        // the flag scoped to THIS surface so one never suppresses the
        // other. `building` has no report threshold today (it fires the
        // review prompt), so the guard below short-circuits for it.
        if (
          reportSurface &&
          reportThreshold !== undefined &&
          isEligibleForReportPrompt &&
          !isReportShownForSurface(nextPrompts, reportSurface) &&
          (nextReport[reportSurface]?.count ?? 0) >= reportThreshold
        ) {
          if (reportReturnUrl) {
            setTriggerReportReturnUrl(reportReturnUrl);
          }
          schedulePrompt('report', reportSurface);
        }

        // Review prompt is per-surface — check the flag scoped to
        // THIS trigger surface, not a single global flag.
        if (
          reviewThreshold !== undefined &&
          isEligibleForReviewPrompt &&
          !isReviewShownForSurface(nextPrompts, pageType) &&
          (nextReview[pageType]?.count ?? 0) >= reviewThreshold
        ) {
          schedulePrompt('review', pageType);
        }

        const next: UiState = { ...current, prompts: nextPrompts };
        persist(next);
        return next;
      });
    },
    [isEligibleForReviewPrompt, isEligibleForReportPrompt, persist, schedulePrompt],
  );

  const markPromptShown = useCallback(
    (prompt: PromptType, surface?: TriggerPageType) => {
      setUiState((current: UiState) => {
        const currentPrompts: PromptsState = current.prompts ?? {};
        let nextPrompts: PromptsState;

        if (prompt === 'review') {
          // Flip the shown flag on this specific surface's bucket.
          const targetSurface = surface ?? 'unit';
          const currentReview: ReviewPromptState = currentPrompts.review ?? {};
          const bucket = currentReview[targetSurface] ?? {};
          const nextReview: ReviewPromptState = {
            ...currentReview,
            [targetSurface]: { ...bucket, shown: true },
          };
          nextPrompts = { ...currentPrompts, review: nextReview };
        } else if (prompt === 'appDownload') {
          // Home is the only app-download surface, so there's nothing
          // to pick — `surface` is ignored here.
          const currentAppDownload: AppDownloadPromptState = currentPrompts.appDownload ?? {};
          nextPrompts = {
            ...currentPrompts,
            appDownload: {
              ...currentAppDownload,
              home: { ...(currentAppDownload.home ?? {}), shown: true },
            },
          };
        } else {
          // Report — flip shown on this specific surface's bucket.
          const targetSurface: ReportSurface =
            surface && isReportSurface(surface) ? surface : 'unit';
          const currentReport: ReportPromptState = currentPrompts.report ?? {};
          const nextReport: ReportPromptState = {
            ...currentReport,
            [targetSurface]: { ...(currentReport[targetSurface] ?? {}), shown: true },
          };
          nextPrompts = { ...currentPrompts, report: nextReport };
        }

        const purged = purgeSeenIfShown(nextPrompts);
        const next: UiState = { ...current, prompts: purged };
        persist(next);
        return next;
      });
      // Cancel any still-pending timer for this prompt.
      const t = pendingTimersRef.current[prompt];
      if (t) {
        clearTimeout(t);
        delete pendingTimersRef.current[prompt];
      }
    },
    [persist],
  );

  const dismissActivePrompt = useCallback(() => {
    setActivePrompt(current => {
      if (current === 'review') {
        // Review dismiss is per-surface — use the surface that was
        // active when the prompt opened. Fall back to 'unit' just to
        // be safe; in practice `activeReviewScreen` is always set
        // alongside `activePrompt = 'review'` (see schedulePrompt).
        markPromptShown('review', activeReviewScreen ?? 'unit');
      } else if (current === 'appDownload') {
        markPromptShown('appDownload', 'home');
      } else if (current) {
        // Report dismiss is per-surface too — same fallback rationale.
        markPromptShown(current, activeReportScreen ?? 'unit');
      }
      return null;
    });
    setActiveReviewScreen(null);
    setActiveReportScreen(null);
  }, [markPromptShown, activeReviewScreen, activeReportScreen]);

  const value: PromptContextValue = {
    onPageView,
    activePrompt,
    activeReviewScreen,
    activeReportScreen,
    triggerReportReturnUrl,
    dismissActivePrompt,
    markPromptShown,
  };

  return <PromptContext.Provider value={value}>{children}</PromptContext.Provider>;
}
